import { useState, useEffect, useCallback } from "react";
import {
  HiOutlineAcademicCap,
  HiOutlinePlus,
  HiOutlineTrash,
  HiOutlinePencil,
  HiOutlineCheck,
  HiOutlineXMark,
  HiOutlineChevronDown,
  HiOutlineChevronRight,
  HiOutlineVideoCamera,
  HiOutlineDocumentText,
  HiOutlineClipboardDocumentCheck,
  HiOutlineQuestionMarkCircle,
  HiOutlineArrowDownTray,
  HiOutlineEye,
  HiOutlineEyeSlash,
  HiOutlineArrowUp,
  HiOutlineArrowDown,
  HiOutlineGlobeAlt,
  HiOutlineLockClosed,
  HiOutlineCheckCircle,
  HiOutlineUserGroup,
} from "react-icons/hi2";
import AdminLayout from "./AdminLayout";
import { coursesAPI } from "../services/api";
import toast from "react-hot-toast";
import "./AdminCourses.css";
import CourseRules from "./courses/CourseRules";

// Thumbnails can be an external absolute URL (pasted manually) or a relative
// uploaded path like "uploads/courses/xxx.jpg" — only the latter needs the
// backend's own origin prepended, since admin and API run on different domains.
const resolveImageUrl = (path) => {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  const base = (process.env.REACT_APP_API_URL || "http://localhost:5000/api").replace(/\/api$/, "");
  return `${base}/${path}`;
};

/* ── Constants ── */
const STATUS_CFG = {
  draft: { label: "Draft", color: "#92400e", bg: "rgba(217,119,6,0.1)" },
  published: { label: "Published", color: "#166534", bg: "rgba(22,163,74,0.1)" },
  archived: { label: "Archived", color: "#6b6258", bg: "rgba(107,98,88,0.1)" },
};
const ACCESS_CFG = {
  public: { label: "Public", Icon: HiOutlineGlobeAlt },
  veterans: { label: "Veterans only", Icon: HiOutlineLockClosed },
  all_applicants: { label: "All applicants", Icon: HiOutlineLockClosed },
  accepted_only: { label: "Accepted only", Icon: HiOutlineLockClosed },
  assigned: { label: "Only who it's given to", Icon: HiOutlineUserGroup },
};
const LESSON_TYPES = [
  { value: "video", label: "Video", Icon: HiOutlineVideoCamera },
  { value: "text", label: "Text", Icon: HiOutlineDocumentText },
  { value: "task", label: "Task", Icon: HiOutlineClipboardDocumentCheck },
  { value: "quiz", label: "Quiz", Icon: HiOutlineQuestionMarkCircle },
  { value: "download", label: "Download", Icon: HiOutlineArrowDownTray },
];
const LESSON_TYPE_MAP = Object.fromEntries(LESSON_TYPES.map((t) => [t.value, t]));

/* ════════════════════════════════════════════════════════
   QUIZ BUILDER HELPERS
   Quiz content is stored as JSON in the lesson.content field:
   [{ id, question, type: "multiple"|"text", options: [{id,text}], correctId }]
════════════════════════════════════════════════════════ */
const makeId = () => Math.random().toString(36).slice(2, 8);

const blankQuestion = () => ({
  id: makeId(),
  question: "",
  type: "multiple", // "multiple" | "text"
  options: [
    { id: makeId(), text: "" },
    { id: makeId(), text: "" },
  ],
  correctId: null, // id of the correct option (multiple choice)
});

const parseQuizContent = (raw) => {
  if (!raw) return [blankQuestion()];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) return parsed;
  } catch (_) {}
  return [blankQuestion()];
};

const serializeQuiz = (questions) => JSON.stringify(questions);

/* ── QuizBuilder component ── */
function QuizBuilder({ value, onChange }) {
  const [questions, setQuestions] = useState(() => parseQuizContent(value));

  // Keep parent form.content in sync
  const update = (updatedQs) => {
    setQuestions(updatedQs);
    onChange(serializeQuiz(updatedQs));
  };

  // Re-parse if value is reset from outside (e.g. after fetch)
  useEffect(() => {
    const parsed = parseQuizContent(value);
    // Only reset if the content actually differs (avoid infinite loop)
    if (JSON.stringify(parsed) !== JSON.stringify(questions)) {
      setQuestions(parsed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const addQuestion = () => update([...questions, blankQuestion()]);

  const removeQuestion = (qid) => {
    if (questions.length === 1) return; // keep at least one
    update(questions.filter((q) => q.id !== qid));
  };

  const updateQ = (qid, patch) => update(questions.map((q) => (q.id === qid ? { ...q, ...patch } : q)));

  const addOption = (qid) => update(questions.map((q) => (q.id === qid ? { ...q, options: [...q.options, { id: makeId(), text: "" }] } : q)));

  const removeOption = (qid, oid) =>
    update(
      questions.map((q) => {
        if (q.id !== qid) return q;
        const opts = q.options.filter((o) => o.id !== oid);
        return { ...q, options: opts, correctId: q.correctId === oid ? null : q.correctId };
      }),
    );

  const updateOption = (qid, oid, text) => update(questions.map((q) => (q.id === qid ? { ...q, options: q.options.map((o) => (o.id === oid ? { ...o, text } : o)) } : q)));

  return (
    <div className="acr__quiz-builder">
      {questions.map((q, qi) => (
        <div key={q.id} className="acr__quiz-question">
          {/* Question header */}
          <div className="acr__quiz-q-head">
            <span className="acr__quiz-q-num">Q{qi + 1}</span>
            <div className="acr__quiz-type-toggle">
              <button type="button" className={`acr__quiz-type-btn ${q.type === "multiple" ? "acr__quiz-type-btn--active" : ""}`} onClick={() => updateQ(q.id, { type: "multiple", correctId: null })}>
                Multiple Choice
              </button>
              <button type="button" className={`acr__quiz-type-btn ${q.type === "text" ? "acr__quiz-type-btn--active" : ""}`} onClick={() => updateQ(q.id, { type: "text" })}>
                Text Answer
              </button>
            </div>
            {questions.length > 1 && (
              <button type="button" className="acr__icon-btn acr__icon-btn--danger acr__quiz-remove-q" onClick={() => removeQuestion(q.id)} title="Remove question">
                <HiOutlineTrash />
              </button>
            )}
          </div>

          {/* Question text */}
          <input className="acr__form-input acr__quiz-q-input" placeholder={`Question ${qi + 1}…`} value={q.question} onChange={(e) => updateQ(q.id, { question: e.target.value })} />

          {/* Options (multiple choice) */}
          {q.type === "multiple" && (
            <div className="acr__quiz-options">
              {q.options.map((opt, oi) => (
                <div key={opt.id} className="acr__quiz-option">
                  {/* Correct answer radio */}
                  <button
                    type="button"
                    className={`acr__quiz-correct-btn ${q.correctId === opt.id ? "acr__quiz-correct-btn--on" : ""}`}
                    onClick={() => updateQ(q.id, { correctId: q.correctId === opt.id ? null : opt.id })}
                    title={q.correctId === opt.id ? "Marked as correct — click to unmark" : "Mark as correct answer"}
                  >
                    {q.correctId === opt.id ? <HiOutlineCheckCircle /> : <span className="acr__quiz-circle" />}
                  </button>
                  <span className="acr__quiz-opt-letter">{String.fromCharCode(65 + oi)}</span>
                  <input
                    className="acr__form-input acr__quiz-opt-input"
                    placeholder={`Option ${String.fromCharCode(65 + oi)}…`}
                    value={opt.text}
                    onChange={(e) => updateOption(q.id, opt.id, e.target.value)}
                  />
                  {q.options.length > 2 && (
                    <button type="button" className="acr__icon-btn acr__icon-btn--danger" onClick={() => removeOption(q.id, opt.id)} title="Remove option">
                      <HiOutlineXMark />
                    </button>
                  )}
                </div>
              ))}
              {q.options.length < 6 && (
                <button type="button" className="acr__quiz-add-opt" onClick={() => addOption(q.id)}>
                  <HiOutlinePlus /> Add option
                </button>
              )}
              {!q.correctId && q.options.length > 0 && <p className="acr__quiz-hint">Click the circle next to the correct answer.</p>}
            </div>
          )}

          {/* Text answer hint */}
          {q.type === "text" && (
            <div className="acr__quiz-text-hint">
              <HiOutlineDocumentText />
              Participants will type a free-text answer. No auto-grading.
            </div>
          )}
        </div>
      ))}

      <button type="button" className="acr__quiz-add-q" onClick={addQuestion}>
        <HiOutlinePlus /> Add Question
      </button>
    </div>
  );
}

/* ── Small helpers ── */
function Badge({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.draft;
  return (
    <span className="acr__badge" style={{ color: cfg.color, background: cfg.bg }}>
      {cfg.label}
    </span>
  );
}

function LessonTypeIcon({ type, size = 16 }) {
  const cfg = LESSON_TYPE_MAP[type] || LESSON_TYPE_MAP.text;
  return <cfg.Icon style={{ fontSize: size, flexShrink: 0 }} />;
}

/* ── Inline editable field ── */
function Editable({ value, onSave, placeholder = "Click to edit…", multiline = false, className = "" }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");

  const commit = () => {
    setEditing(false);
    if (draft !== value) onSave(draft);
  };
  const cancel = () => {
    setEditing(false);
    setDraft(value || "");
  };

  if (!editing) {
    return (
      <span
        className={`acr__editable ${className}`}
        onClick={() => {
          setDraft(value || "");
          setEditing(true);
        }}
        title="Click to edit"
      >
        {value || <em className="acr__editable-ph">{placeholder}</em>}
        <HiOutlinePencil className="acr__editable-icon" />
      </span>
    );
  }
  return (
    <span className="acr__editable acr__editable--editing">
      {multiline ? (
        <textarea
          autoFocus
          className="acr__inline-input acr__inline-textarea"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") cancel();
          }}
          rows={3}
        />
      ) : (
        <input
          autoFocus
          className="acr__inline-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") cancel();
          }}
        />
      )}
      <button type="button" className="acr__inline-ok" onClick={commit}>
        <HiOutlineCheck />
      </button>
      <button type="button" className="acr__inline-no" onClick={cancel}>
        <HiOutlineXMark />
      </button>
    </span>
  );
}

/* ════════════════════════════════════════════════════════
   LESSON EDITOR — right pane
════════════════════════════════════════════════════════ */
function LessonEditor({ lesson, courseId, chapterId, onUpdate, onClose }) {
  // Always start from the lesson prop so the panel shows immediately,
  // then fetch full data from DB to get content/video_url that may not
  // be in the in-memory lesson object (e.g. after addLesson returns minimal fields).
  const [form, setForm] = useState({
    title: lesson.title || "",
    type: lesson.type || "text",
    content: lesson.content || "",
    video_url: lesson.video_url || "",
    duration_min: lesson.duration_min ?? "",
    is_required: lesson.is_required !== 0,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Fetch the full lesson record from the DB so content/video_url are populated.
  // This runs every time the lesson.id changes (i.e. a different lesson is opened).
  useEffect(() => {
    // Only fetch if we suspect the in-memory object is incomplete
    // (i.e. content is falsy but lesson was previously saved — we can't know for sure,
    // so we always fetch to guarantee freshness).
    let cancelled = false;
    const fetchFull = async () => {
      setLoading(true);
      try {
        // GET /api/courses/:courseId — returns chapters with lessons including all fields
        const res = await coursesAPI.getOne(courseId);
        if (cancelled) return;
        const chapters = res.data?.chapters || [];
        const chapter = chapters.find((ch) => ch.id === chapterId);
        const full = chapter?.lessons?.find((l) => l.id === lesson.id);
        if (full) {
          setForm({
            title: full.title || "",
            type: full.type || "text",
            content: full.content || "",
            video_url: full.video_url || "",
            duration_min: full.duration_min ?? "",
            is_required: full.is_required !== 0,
          });
        }
      } catch (_) {
        // If fetch fails, the form already has what the prop gave us — good enough
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchFull();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesson.id]);

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error("Lesson title is required.");
      return;
    }
    setSaving(true);
    try {
      const res = await coursesAPI.updateLesson(courseId, chapterId, lesson.id, {
        ...form,
        duration_min: form.duration_min ? parseInt(form.duration_min) : null,
        is_required: form.is_required ? 1 : 0,
      });
      const saved = res.data || { ...lesson, ...form };
      // Sync local form so re-opening shows saved data without a Back
      setForm({
        title: saved.title || "",
        type: saved.type || "text",
        content: saved.content || "",
        video_url: saved.video_url || "",
        duration_min: saved.duration_min ?? "",
        is_required: saved.is_required !== 0,
      });
      onUpdate(saved);
      toast.success("Lesson saved.");
    } catch (err) {
      toast.error("Failed: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="acr__lesson-editor">
      <div className="acr__le-head">
        <div className="acr__le-head-left">
          <LessonTypeIcon type={form.type} size={18} />
          <span className="acr__le-title">Edit Lesson</span>
        </div>
        <button type="button" className="acr__icon-btn" onClick={onClose}>
          <HiOutlineXMark />
        </button>
      </div>

      <div className="acr__le-body">
        {loading && (
          <div className="acr__le-loading">
            <div className="acr__spinner" style={{ width: "1.25rem", height: "1.25rem" }} />
            <span>Loading lesson…</span>
          </div>
        )}
        {/* Title */}
        <div className="acr__form-field">
          <label className="acr__form-label">
            Title <span className="acr__req">*</span>
          </label>
          <input className="acr__form-input" value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} placeholder="Lesson title" />
        </div>

        {/* Type */}
        <div className="acr__form-field">
          <label className="acr__form-label">Type</label>
          <div className="acr__type-grid">
            {LESSON_TYPES.map((t) => (
              <button key={t.value} type="button" className={`acr__type-btn ${form.type === t.value ? "acr__type-btn--active" : ""}`} onClick={() => setForm((p) => ({ ...p, type: t.value }))}>
                <t.Icon className="acr__type-icon" />
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Video URL — only for video type */}
        {form.type === "video" && (
          <div className="acr__form-field">
            <label className="acr__form-label">Video URL</label>
            <input className="acr__form-input" value={form.video_url} onChange={(e) => setForm((p) => ({ ...p, video_url: e.target.value }))} placeholder="https://youtube.com/embed/… or Vimeo URL" />
            <p className="acr__form-hint">Paste a YouTube embed URL, Vimeo, or direct MP4 link.</p>
          </div>
        )}

        {/* Content — quiz gets the builder, everything else gets a textarea */}
        {form.type === "quiz" ? (
          <div className="acr__form-field">
            <label className="acr__form-label">Quiz Questions</label>
            <QuizBuilder value={form.content} onChange={(val) => setForm((p) => ({ ...p, content: val }))} />
          </div>
        ) : (
          <div className="acr__form-field">
            <label className="acr__form-label">
              {form.type === "video" ? "Description / Transcript" : form.type === "task" ? "Task Instructions" : form.type === "download" ? "File Description / Instructions" : "Content"}
            </label>
            <textarea
              className="acr__form-input acr__form-textarea"
              value={form.content}
              onChange={(e) => setForm((p) => ({ ...p, content: e.target.value }))}
              placeholder={
                form.type === "task"
                  ? "Describe what the participant needs to do…"
                  : form.type === "video"
                    ? "Optional: Add a description or transcript for accessibility…"
                    : "Write your lesson content here. Markdown is supported."
              }
              rows={10}
            />
          </div>
        )}

        {/* Duration + required */}
        <div className="acr__form-row">
          <div className="acr__form-field" style={{ flex: 1 }}>
            <label className="acr__form-label">Duration (minutes)</label>
            <input className="acr__form-input" type="number" min="1" value={form.duration_min} onChange={(e) => setForm((p) => ({ ...p, duration_min: e.target.value }))} placeholder="e.g. 15" />
          </div>
          <div className="acr__form-field acr__form-field--toggle">
            <label className="acr__form-label">Required</label>
            <button type="button" className={`acr__toggle ${form.is_required ? "acr__toggle--on" : ""}`} onClick={() => setForm((p) => ({ ...p, is_required: !p.is_required }))}>
              <span className="acr__toggle-thumb" />
            </button>
            <span className="acr__toggle-label">{form.is_required ? "Required" : "Optional"}</span>
          </div>
        </div>
      </div>

      <div className="acr__le-footer">
        <button type="button" className="a-btn a-btn--outline" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="a-btn a-btn--rust" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save Lesson"}
        </button>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   CHAPTER ROW — collapsible with lessons
════════════════════════════════════════════════════════ */
function ChapterRow({ chapter, courseId, idx, total, onUpdate, onDelete, onMoveUp, onMoveDown, editingLesson, setEditingLesson }) {
  const [open, setOpen] = useState(true);
  const [addingLesson, setAddingLesson] = useState(false);
  const [newLTitle, setNewLTitle] = useState("");
  const [newLType, setNewLType] = useState("text");
  const [lessons, setLessons] = useState(chapter.lessons || []);
  const [saving, setSaving] = useState(false);

  const handleSaveTitle = async (val) => {
    if (!val.trim()) return;
    try {
      await coursesAPI.updateChapter(courseId, chapter.id, { title: val.trim() });
      onUpdate({ ...chapter, title: val.trim(), lessons });
      toast.success("Chapter updated.");
    } catch (err) {
      toast.error("Failed: " + err.message);
    }
  };

  const handleAddLesson = async () => {
    if (!newLTitle.trim()) return;
    setSaving(true);
    try {
      const res = await coursesAPI.addLesson(courseId, chapter.id, { title: newLTitle.trim(), type: newLType });
      const newLesson = res.data;
      setLessons((prev) => [...prev, newLesson]);
      setNewLTitle("");
      setAddingLesson(false);
      toast.success("Lesson added.");
    } catch (err) {
      toast.error("Failed: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLesson = async (lid) => {
    toast(
      (t) => (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          <p style={{ margin: 0, fontWeight: 700 }}>Delete this lesson?</p>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button
              onClick={() => {
                toast.dismiss(t.id);
                doDeleteLesson(lid);
              }}
              style={{ background: "#dc2626", color: "#fff", border: "none", borderRadius: "5px", padding: "0.35rem 0.75rem", cursor: "pointer", fontWeight: 700, fontSize: "0.78rem" }}
            >
              Delete
            </button>
            <button onClick={() => toast.dismiss(t.id)} style={{ background: "#f3f4f0", border: "none", borderRadius: "5px", padding: "0.35rem 0.75rem", cursor: "pointer", fontSize: "0.78rem" }}>
              Cancel
            </button>
          </div>
        </div>
      ),
      { duration: Infinity, style: { maxWidth: "280px" } },
    );
  };

  const doDeleteLesson = async (lid) => {
    try {
      await coursesAPI.removeLesson(courseId, chapter.id, lid);
      setLessons((prev) => prev.filter((l) => l.id !== lid));
      if (editingLesson?.lesson?.id === lid) setEditingLesson(null);
      toast.success("Lesson deleted.");
    } catch (err) {
      toast.error("Failed: " + err.message);
    }
  };

  // Kept for the lesson editor's save callback — not wired up yet.
  // eslint-disable-next-line no-unused-vars
  const handleLessonUpdated = (updated) => {
    setLessons((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
    setEditingLesson(null);
  };

  const moveLessonUp = async (i) => {
    if (i === 0) return;
    const updated = [...lessons];
    [updated[i - 1], updated[i]] = [updated[i], updated[i - 1]];
    setLessons(updated);
    await coursesAPI.sortLessons(
      courseId,
      chapter.id,
      updated.map((l, idx) => ({ id: l.id, sort_order: idx })),
    );
  };
  const moveLessonDown = async (i) => {
    if (i >= lessons.length - 1) return;
    const updated = [...lessons];
    [updated[i], updated[i + 1]] = [updated[i + 1], updated[i]];
    setLessons(updated);
    await coursesAPI.sortLessons(
      courseId,
      chapter.id,
      updated.map((l, idx) => ({ id: l.id, sort_order: idx })),
    );
  };

  return (
    <div className="acr__chapter">
      {/* Chapter header */}
      <div className="acr__chapter-head">
        <button type="button" className="acr__chapter-toggle" onClick={() => setOpen((o) => !o)}>
          {open ? <HiOutlineChevronDown /> : <HiOutlineChevronRight />}
        </button>
        <span className="acr__chapter-num">Ch {idx + 1}</span>
        <Editable value={chapter.title} onSave={handleSaveTitle} placeholder="Chapter title" className="acr__chapter-title-editable" />
        <div className="acr__chapter-actions">
          <span className="acr__chapter-count">
            {lessons.length} lesson{lessons.length !== 1 ? "s" : ""}
          </span>
          <button type="button" className="acr__icon-btn" onClick={onMoveUp} disabled={idx === 0} title="Move up">
            {" "}
            <HiOutlineArrowUp />
          </button>
          <button type="button" className="acr__icon-btn" onClick={onMoveDown} disabled={idx === total - 1} title="Move down">
            {" "}
            <HiOutlineArrowDown />
          </button>
          <button type="button" className="acr__icon-btn acr__icon-btn--danger" onClick={onDelete} title="Delete chapter">
            <HiOutlineTrash />
          </button>
        </div>
      </div>

      {/* Lessons */}
      {open && (
        <div className="acr__lessons">
          {lessons.length === 0 && !addingLesson && <p className="acr__lessons-empty">No lessons yet. Add one below.</p>}
          {lessons.map((l, li) => (
            <div
              key={l.id}
              className={`acr__lesson-row ${editingLesson?.lesson?.id === l.id ? "acr__lesson-row--active" : ""}`}
              onClick={() => setEditingLesson(editingLesson?.lesson?.id === l.id ? null : { lesson: l, chapterId: chapter.id })}
            >
              <div className="acr__lesson-left">
                <span className="acr__lesson-num">{li + 1}</span>
                <LessonTypeIcon type={l.type} />
                <span className="acr__lesson-title">{l.title}</span>
                {!l.is_required && <span className="acr__lesson-opt">Optional</span>}
                {l.duration_min && <span className="acr__lesson-dur">{l.duration_min}m</span>}
              </div>
              <div className="acr__lesson-actions" onClick={(e) => e.stopPropagation()}>
                <button type="button" className="acr__icon-btn" onClick={() => moveLessonUp(li)} disabled={li === 0} title="Move up">
                  {" "}
                  <HiOutlineArrowUp />
                </button>
                <button type="button" className="acr__icon-btn" onClick={() => moveLessonDown(li)} disabled={li === lessons.length - 1} title="Move down">
                  <HiOutlineArrowDown />
                </button>
                <button type="button" className="acr__icon-btn" onClick={() => setEditingLesson(editingLesson?.lesson?.id === l.id ? null : { lesson: l, chapterId: chapter.id })} title="Edit">
                  <HiOutlinePencil />
                </button>
                <button type="button" className="acr__icon-btn acr__icon-btn--danger" onClick={() => handleDeleteLesson(l.id)} title="Delete">
                  <HiOutlineTrash />
                </button>
              </div>
            </div>
          ))}

          {/* Add lesson form */}
          {addingLesson ? (
            <div className="acr__add-lesson-form">
              <div className="acr__add-lesson-types">
                {LESSON_TYPES.map((t) => (
                  <button key={t.value} type="button" className={`acr__add-type-btn ${newLType === t.value ? "acr__add-type-btn--active" : ""}`} onClick={() => setNewLType(t.value)}>
                    <t.Icon /> {t.label}
                  </button>
                ))}
              </div>
              <div className="acr__add-lesson-row">
                <input
                  className="acr__form-input"
                  autoFocus
                  placeholder="Lesson title…"
                  value={newLTitle}
                  onChange={(e) => setNewLTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleAddLesson();
                    if (e.key === "Escape") setAddingLesson(false);
                  }}
                />
                <button type="button" className="a-btn a-btn--rust" onClick={handleAddLesson} disabled={saving || !newLTitle.trim()}>
                  {saving ? "Adding…" : "Add"}
                </button>
                <button
                  type="button"
                  className="a-btn a-btn--outline"
                  onClick={() => {
                    setAddingLesson(false);
                    setNewLTitle("");
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button type="button" className="acr__add-lesson-btn" onClick={() => setAddingLesson(true)}>
              <HiOutlinePlus /> Add Lesson
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   COURSE EDITOR — center pane
════════════════════════════════════════════════════════ */
function CourseEditor({ courseId, onBack }) {
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addingChapter, setAddingChapter] = useState(false);
  const [newChTitle, setNewChTitle] = useState("");
  const [editingLesson, setEditingLesson] = useState(null); // { lesson, chapterId }
  const [showSettings, setShowSettings] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [settingsForm, setSettingsForm] = useState({});

  const fetchCourse = useCallback(async () => {
    setLoading(true);
    try {
      const res = await coursesAPI.getOne(courseId);
      setCourse(res.data);
      setSettingsForm({
        description: res.data.description || "",
        thumbnail: res.data.thumbnail || "",
        status: res.data.status || "draft",
        access: res.data.access || "accepted_only",
        share_makua: !!res.data.share_makua,
      });
    } catch (err) {
      toast.error("Failed to load course.");
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    fetchCourse();
  }, [fetchCourse]);

  const handleSaveTitle = async (val) => {
    if (!val.trim()) return;
    try {
      await coursesAPI.update(courseId, { title: val.trim() });
      setCourse((prev) => ({ ...prev, title: val.trim() }));
      toast.success("Title updated.");
    } catch (err) {
      toast.error("Failed: " + err.message);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await coursesAPI.update(courseId, settingsForm);
      setCourse((prev) => ({ ...prev, ...settingsForm }));
      setShowSettings(false);
      toast.success("Course settings saved.");
    } catch (err) {
      toast.error("Failed: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddChapter = async () => {
    if (!newChTitle.trim()) return;
    setSaving(true);
    try {
      const res = await coursesAPI.addChapter(courseId, { title: newChTitle.trim() });
      setCourse((prev) => ({ ...prev, chapters: [...(prev.chapters || []), res.data] }));
      setNewChTitle("");
      setAddingChapter(false);
      toast.success("Chapter added.");
    } catch (err) {
      toast.error("Failed: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteChapter = async (chId) => {
    toast(
      (t) => (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          <p style={{ margin: 0, fontWeight: 700 }}>Delete this chapter and all its lessons?</p>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button
              onClick={() => {
                toast.dismiss(t.id);
                doDeleteChapter(chId);
              }}
              style={{ background: "#dc2626", color: "#fff", border: "none", borderRadius: "5px", padding: "0.35rem 0.75rem", cursor: "pointer", fontWeight: 700, fontSize: "0.78rem" }}
            >
              Delete
            </button>
            <button onClick={() => toast.dismiss(t.id)} style={{ background: "#f3f4f0", border: "none", borderRadius: "5px", padding: "0.35rem 0.75rem", cursor: "pointer", fontSize: "0.78rem" }}>
              Cancel
            </button>
          </div>
        </div>
      ),
      { duration: Infinity, style: { maxWidth: "300px" } },
    );
  };

  const doDeleteChapter = async (chId) => {
    try {
      await coursesAPI.removeChapter(courseId, chId);
      setCourse((prev) => ({ ...prev, chapters: prev.chapters.filter((c) => c.id !== chId) }));
      toast.success("Chapter deleted.");
    } catch (err) {
      toast.error("Failed: " + err.message);
    }
  };

  const moveChapterUp = async (i) => {
    if (!course || i === 0) return;
    const updated = [...course.chapters];
    [updated[i - 1], updated[i]] = [updated[i], updated[i - 1]];
    setCourse((prev) => ({ ...prev, chapters: updated }));
    await coursesAPI.sortChapters(
      courseId,
      updated.map((c, idx) => ({ id: c.id, sort_order: idx })),
    );
  };
  const moveChapterDown = async (i) => {
    if (!course || i >= course.chapters.length - 1) return;
    const updated = [...course.chapters];
    [updated[i], updated[i + 1]] = [updated[i + 1], updated[i]];
    setCourse((prev) => ({ ...prev, chapters: updated }));
    await coursesAPI.sortChapters(
      courseId,
      updated.map((c, idx) => ({ id: c.id, sort_order: idx })),
    );
  };

  const totalLessons = course?.chapters?.reduce((s, c) => s + (c.lessons?.length || 0), 0) || 0;

  if (loading)
    return (
      <div className="acr__editor-loading">
        <div className="acr__spinner" />
      </div>
    );
  if (!course) return null;

  // Use the chapterId stored directly on the editingLesson pair — don't search
  // course.chapters for the lesson (those lessons can be stale if added via ChapterRow local state)
  const editingChapterId = editingLesson?.chapterId ?? null;

  return (
    <div className={`acr__editor ${editingLesson ? "acr__editor--with-panel" : ""}`}>
      {/* ── Editor main ── */}
      <div className="acr__editor-main">
        {/* Header */}
        <div className="acr__editor-head">
          <button type="button" className="acr__back-btn" onClick={onBack}>
            ← All Courses
          </button>
          <div className="acr__editor-head-right">
            <Badge status={course.status} />
            <button type="button" className="a-btn a-btn--outline acr__settings-btn" aria-expanded={showRules} onClick={() => setShowRules((s) => !s)}>
              <HiOutlineUserGroup /> {showRules ? "Hide who gets it" : "Who gets it"}
            </button>
            <button type="button" className="a-btn a-btn--outline acr__settings-btn" onClick={() => setShowSettings((s) => !s)}>
              {showSettings ? <HiOutlineEyeSlash /> : <HiOutlineEye />}
              {showSettings ? "Hide Settings" : "Settings"}
            </button>
          </div>
        </div>

        {/* Course title */}
        <div className="acr__editor-title-wrap">
          <HiOutlineAcademicCap className="acr__editor-title-icon" />
          <Editable value={course.title} onSave={handleSaveTitle} placeholder="Course title" className="acr__course-title-editable" />
        </div>

        {/* Stats bar */}
        <div className="acr__editor-stats">
          <span>
            {course.chapters?.length || 0} chapter{course.chapters?.length !== 1 ? "s" : ""}
          </span>
          <span className="acr__stat-dot">·</span>
          <span>
            {totalLessons} lesson{totalLessons !== 1 ? "s" : ""}
          </span>
          <span className="acr__stat-dot">·</span>
          <span>{ACCESS_CFG[course.access]?.label || course.access}</span>
          {course.share_makua && course.status === "published" && (
            <>
              <span className="acr__stat-dot">·</span>
              <span>Offered to Makua</span>
            </>
          )}
        </div>

        {showRules && (
          <div className="acr__rules">
            <CourseRules
              courseId={course.id}
              canEdit
              intro={course.access === "assigned"
                ? "Only the people these rules reach get this course."
                : `These rules add people on top of the access level (${ACCESS_CFG[course.access]?.label || course.access}). For a course only they should see, set Access to "Only who it's given to" in Settings.`}
            />
          </div>
        )}

        {/* Settings panel */}
        {showSettings && (
          <div className="acard acr__settings-panel">
            <p className="acr__settings-title">Course Settings</p>
            <div className="acr__settings-grid">
              <div className="acr__form-field">
                <label className="acr__form-label">Description</label>
                <textarea
                  className="acr__form-input acr__form-textarea"
                  rows={3}
                  value={settingsForm.description}
                  onChange={(e) => setSettingsForm((p) => ({ ...p, description: e.target.value }))}
                  placeholder="Brief description of this course…"
                />
              </div>
              <div className="acr__form-field">
                <label className="acr__form-label">Thumbnail URL</label>
                <input className="acr__form-input" value={settingsForm.thumbnail} onChange={(e) => setSettingsForm((p) => ({ ...p, thumbnail: e.target.value }))} placeholder="https://…/image.jpg" />
              </div>
              <div className="acr__settings-row">
                <div className="acr__form-field">
                  <label className="acr__form-label">Status</label>
                  <select className="acr__form-input acr__form-select" value={settingsForm.status} onChange={(e) => setSettingsForm((p) => ({ ...p, status: e.target.value }))}>
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                <div className="acr__form-field">
                  <label className="acr__form-label">Access</label>
                  <select className="acr__form-input acr__form-select" value={settingsForm.access} onChange={(e) => setSettingsForm((p) => ({ ...p, access: e.target.value }))}>
                    <option value="public">Public</option>
                    <option value="veterans">Veterans only</option>
                    <option value="all_applicants">All applicants</option>
                    <option value="accepted_only">Accepted only</option>
                    <option value="assigned">Only who it's given to</option>
                  </select>
                </div>
              </div>
              <label className="acr__share">
                <input type="checkbox" checked={!!settingsForm.share_makua} onChange={(e) => setSettingsForm((p) => ({ ...p, share_makua: e.target.checked }))} />
                <span>
                  <b>Also offer to Makua</b>
                  <em>Makua's team can then give this course to their guests. They see it once it's published; their guests' progress stays with Makua.</em>
                </span>
              </label>
            </div>
            <div className="acr__settings-footer">
              <button type="button" className="a-btn a-btn--outline" onClick={() => setShowSettings(false)}>
                Cancel
              </button>
              <button type="button" className="a-btn a-btn--rust" onClick={handleSaveSettings} disabled={saving}>
                {saving ? "Saving…" : "Save Settings"}
              </button>
            </div>
          </div>
        )}

        {/* Chapters */}
        <div className="acr__chapters">
          {(!course.chapters || course.chapters.length === 0) && (
            <div className="acr__empty-chapters">
              <HiOutlineAcademicCap className="acr__empty-icon" />
              <p className="acr__empty-title">No chapters yet</p>
              <p className="acr__empty-sub">Add your first chapter to start building this course.</p>
            </div>
          )}

          {(course.chapters || []).map((ch, i) => (
            <ChapterRow
              key={ch.id}
              chapter={ch}
              courseId={courseId}
              idx={i}
              total={course.chapters.length}
              onUpdate={(updated) =>
                setCourse((prev) => ({
                  ...prev,
                  chapters: prev.chapters.map((c) => (c.id === updated.id ? updated : c)),
                }))
              }
              onDelete={() => handleDeleteChapter(ch.id)}
              onMoveUp={() => moveChapterUp(i)}
              onMoveDown={() => moveChapterDown(i)}
              editingLesson={editingLesson}
              setEditingLesson={(l) => setEditingLesson(l)}
            />
          ))}

          {/* Add chapter */}
          {addingChapter ? (
            <div className="acr__add-chapter-form">
              <input
                className="acr__form-input"
                autoFocus
                placeholder="Chapter title…"
                value={newChTitle}
                onChange={(e) => setNewChTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAddChapter();
                  if (e.key === "Escape") {
                    setAddingChapter(false);
                    setNewChTitle("");
                  }
                }}
              />
              <button type="button" className="a-btn a-btn--rust" onClick={handleAddChapter} disabled={saving || !newChTitle.trim()}>
                {saving ? "Adding…" : "Add Chapter"}
              </button>
              <button
                type="button"
                className="a-btn a-btn--outline"
                onClick={() => {
                  setAddingChapter(false);
                  setNewChTitle("");
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button type="button" className="acr__add-chapter-btn" onClick={() => setAddingChapter(true)}>
              <HiOutlinePlus /> Add Chapter
            </button>
          )}
        </div>
      </div>

      {/* ── Lesson editor side panel ── */}
      {editingLesson && editingChapterId && (
        <LessonEditor
          lesson={editingLesson.lesson}
          courseId={courseId}
          chapterId={editingChapterId}
          onUpdate={(updated) => {
            setCourse((prev) => ({
              ...prev,
              chapters: prev.chapters.map((ch) => (ch.id === editingChapterId ? { ...ch, lessons: (ch.lessons || []).map((l) => (l.id === updated.id ? updated : l)) } : ch)),
            }));
            setEditingLesson(null);
          }}
          onClose={() => setEditingLesson(null)}
        />
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   MAIN — AdminCourses
════════════════════════════════════════════════════════ */
export default function AdminCourses() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [editingId, setEditingId] = useState(null); // course ID being edited

  const fetchCourses = useCallback(async () => {
    setLoading(true);
    try {
      const res = await coursesAPI.list();
      setCourses(res.data || []);
    } catch (err) {
      toast.error("Failed to load courses.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  const handleCreate = async () => {
    if (!newTitle.trim()) {
      toast.error("Course title is required.");
      return;
    }
    setCreating(true);
    try {
      const res = await coursesAPI.create({ title: newTitle.trim() });
      setCourses((prev) => [res.data, ...prev]);
      setNewTitle("");
      setEditingId(res.data.id); // go straight into editor
      toast.success("Course created.");
    } catch (err) {
      toast.error("Failed: " + err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = (id) => {
    toast(
      (t) => (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          <p style={{ margin: 0, fontWeight: 700 }}>Delete this course?</p>
          <p style={{ margin: 0, fontSize: "0.78rem", color: "#6b6258" }}>All chapters and lessons will be permanently deleted.</p>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button
              onClick={() => {
                toast.dismiss(t.id);
                doDelete(id);
              }}
              style={{ background: "#dc2626", color: "#fff", border: "none", borderRadius: "5px", padding: "0.35rem 0.75rem", cursor: "pointer", fontWeight: 700, fontSize: "0.78rem" }}
            >
              Delete
            </button>
            <button onClick={() => toast.dismiss(t.id)} style={{ background: "#f3f4f0", border: "none", borderRadius: "5px", padding: "0.35rem 0.75rem", cursor: "pointer", fontSize: "0.78rem" }}>
              Cancel
            </button>
          </div>
        </div>
      ),
      { duration: Infinity, style: { maxWidth: "320px" } },
    );
  };

  const doDelete = async (id) => {
    try {
      await coursesAPI.remove(id);
      setCourses((prev) => prev.filter((c) => c.id !== id));
      toast.success("Course deleted.");
    } catch (err) {
      toast.error("Failed: " + err.message);
    }
  };

  // ── If editing a course, show the editor instead of the list ──
  if (editingId) {
    return (
      <AdminLayout pageTitle="Courses" pageSubtitle="Build and manage learning content.">
        <CourseEditor
          courseId={editingId}
          onBack={() => {
            setEditingId(null);
            fetchCourses();
          }}
        />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout pageTitle="Courses" pageSubtitle="Create and manage courses, chapters, and lessons.">
      {/* Stat bar */}
      <div className="acr__stat-bar">
        <div className="acard acr__stat-card">
          <p className="acr__stat-label">Total Courses</p>
          <p className="acr__stat-value">{courses.length}</p>
        </div>
        <div className="acard acr__stat-card">
          <p className="acr__stat-label">Published</p>
          <p className="acr__stat-value acr__stat-value--green">{courses.filter((c) => c.status === "published").length}</p>
        </div>
        <div className="acard acr__stat-card">
          <p className="acr__stat-label">Drafts</p>
          <p className="acr__stat-value acr__stat-value--amber">{courses.filter((c) => c.status === "draft").length}</p>
        </div>
        <div className="acard acr__stat-card acr__stat-card--cta">
          {newTitle !== null && (
            <div className="acr__new-course-row">
              <input
                className="acr__form-input"
                placeholder="New course title…"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCreate();
                }}
              />
              <button type="button" className="a-btn a-btn--rust" onClick={handleCreate} disabled={creating || !newTitle.trim()}>
                <HiOutlinePlus /> {creating ? "Creating…" : "Create Course"}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Course list */}
      {loading ? (
        <div className="acr__loading-wrap">
          <div className="acr__spinner" />
        </div>
      ) : courses.length === 0 ? (
        <div className="acard acr__empty">
          <HiOutlineAcademicCap className="acr__empty-icon" />
          <p className="acr__empty-title">No courses yet</p>
          <p className="acr__empty-sub">Create your first course above to get started.</p>
        </div>
      ) : (
        <div className="acr__course-list">
          {courses.map((course) => (
            <div key={course.id} className="acard acr__course-card">
              {course.thumbnail && (
                <div className="acr__course-thumb">
                  <img src={resolveImageUrl(course.thumbnail)} alt="" onError={(e) => (e.target.style.display = "none")} />
                </div>
              )}
              <div className="acr__course-body">
                <div className="acr__course-top">
                  <h3 className="acr__course-title">{course.title}</h3>
                  <Badge status={course.status} />
                </div>
                {course.description && <p className="acr__course-desc">{course.description}</p>}
                <div className="acr__course-meta">
                  <span>
                    {course.chapter_count || 0} chapter{course.chapter_count !== 1 ? "s" : ""}
                  </span>
                  <span className="acr__stat-dot">·</span>
                  <span>
                    {course.lesson_count || 0} lesson{course.lesson_count !== 1 ? "s" : ""}
                  </span>
                  <span className="acr__stat-dot">·</span>
                  <span>{ACCESS_CFG[course.access]?.label || course.access}</span>
                </div>
              </div>
              <div className="acr__course-actions">
                <button type="button" className="a-btn a-btn--rust" onClick={() => setEditingId(course.id)}>
                  <HiOutlinePencil /> Edit
                </button>
                <button type="button" className="a-btn a-btn--outline acr__delete-btn" onClick={() => handleDelete(course.id)} aria-label={`Delete ${course.title}`} title="Delete">
                  <HiOutlineTrash />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
